import { Slot } from '@radix-ui/react-slot';
import { VariantProps, cva } from 'class-variance-authority';

import { SolidCircleInfo, SolidTriangleExclamation } from '@/icons/components';
import { IconComponent } from '@/icons/iconTypes';
import { createScopedContext } from '@/utils/createScopedContext/createScopedContext';
import { twJoin, twMerge } from '@/utils/tw-merge';

const warningVariants = cva('flex items-start rounded-sm border', {
  variants: {
    variant: {
      error: 'border-red-100 bg-red-50 text-red-800',
      info: 'border-blue-100 bg-blue-50 text-blue-500',
      warning: 'border-yellow-200 bg-yellow-50 text-gray-900',
      success: 'border-green-100 bg-green-50 text-green-800',
    },
    size: {
      small: 'gap-1 px-1 py-0.5',
      regular: 'gap-1 px-2 py-1',
      large: 'gap-2 p-2',
    },
  },
  defaultVariants: {
    variant: 'info',
    size: 'regular',
  },
});

type NoUndefinedField<T> = {
  [P in keyof T]-?: NoUndefinedField<NonNullable<T[P]>>;
};
type RequiredVariantProps = NoUndefinedField<
  VariantProps<typeof warningVariants>
>;

const [WarningProvider, useWarningContext] =
  createScopedContext<Pick<RequiredVariantProps, 'size'>>();

export interface WarningProps
  extends React.HTMLAttributes<HTMLDivElement>,
    RequiredVariantProps {}

export const Warning = ({
  variant = 'info',
  size = 'regular',
  className,
  children,
  ...props
}: WarningProps) => {
  let Icon: IconComponent | undefined;

  switch (variant) {
    case 'error':
      Icon = SolidTriangleExclamation;
      break;
    case 'warning':
      Icon = SolidTriangleExclamation;
      break;
    case 'success':
      Icon = SolidCircleInfo;
      break;
    case 'info':
      Icon = SolidCircleInfo;
      break;
  }

  const iconSize = {
    small: 14,
    regular: 16,
    large: 18,
  };

  const iconClassname = {
    small: 'mt-[3px]',
    regular: 'mt-[4px]',
    large: 'mt-[3px]',
  };

  return (
    <WarningProvider size={size}>
      <div
        className={twJoin(warningVariants({ variant, size, className }))}
        {...props}
      >
        {Icon && (
          <Icon
            className={twJoin('block shrink-0', iconClassname[size])}
            size={iconSize[size]}
          />
        )}

        <div className="flex flex-col items-start gap-1">{children}</div>
      </div>
    </WarningProvider>
  );
};

Warning.Title = function Title({
  asChild,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { asChild?: boolean }) {
  const Component = asChild ? Slot : 'h2';
  const { size } = useWarningContext();

  const classes = twJoin(
    size === 'small' && 'style-body-small',
    size === 'regular' && 'style-body',
    size === 'large' && 'style-h5-bold'
  );

  return <Component className={twMerge(classes, className)} {...props} />;
};

Warning.Description = function Description({
  className,
  asChild,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { asChild?: boolean }) {
  const Component = asChild ? Slot : 'p';

  return (
    <Component className={twMerge('style-body-small', className)} {...props} />
  );
};
